import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BackButton from "../../../../BaseComponents/BackButton";
import StaffAssignPanel from "../../../../BaseComponents/StaffAssignPanel";
import { checkStatusCodeSuccess } from "../../../../Constant/common";
import { editLeaveStaffListApi, viewLeaveStaffListApi } from "../../../../Api/leaveTemplate";
import { leaveTemplate } from "../../../../Constant/HR/leaveTemplate";
import { useSelectedCenter } from "../../../CenterContext";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import { GitBranch } from "react-feather";

const ManageLeaveStaff = () => {
  const { leaveId } = useParams();
  const navigate = useNavigate();
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();

  const [withLeave, setWithLeave] = useState([]);
  const [withoutLeave, setWithoutLeave] = useState([]);
  const [searchWith, setSearchWith] = useState("");
  const [searchWithout, setSearchWithout] = useState("");
  const [btnLoader, setBtnLoader] = useState(false);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const isInitialLoad = useRef(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    viewLeaveStaffListApi(leaveId, {
      ...selectedCenterPayload,
      selectedStaffSearch: searchWith,
      unselectedStaffSearch: searchWithout,
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) {
          const selectedStaff = res?.data?.selectedStaff ?? [];
          const unselectedStaff = res?.data?.unselectedStaff ?? [];
          setWithLeave(selectedStaff);
          setWithoutLeave(unselectedStaff);
          // Store initial selected IDs only on first load
          if (isInitialLoad.current) {
            setInitialSelectedIds(selectedStaff.map((s) => s.id));
            isInitialLoad.current = false;
          }
        } else {
          setWithLeave([]);
          setWithoutLeave([]);
          toast.error(res?.message);
        }
      })
      .catch(() => {
        setWithLeave([]);
        setWithoutLeave([]);
      })
      .finally(() => setLoading(false));
  }, [leaveId, selectedCenterPayload, searchWith, searchWithout, setLoading]);

  useEffect(() => {
    const t = setTimeout(fetchData, 0);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggle = (staff, isWith) => {
    const setter = isWith ? setWithLeave : setWithoutLeave;
    setter((prev) => prev.map((s) => (s.id === staff.id ? { ...s, selected: !s.selected } : s)));
  };

  const checkAll = (isWith) => {
    const list = isWith ? withLeave : withoutLeave;
    const setter = isWith ? setWithLeave : setWithoutLeave;
    const allOn = list.every((s) => s.selected);
    setter((prev) => prev.map((s) => ({ ...s, selected: !allOn })));
  };

  const getIds = (list, sel) => list.filter((s) => s.selected === sel).map((s) => s.id);

  const handleSave = () => {
    setBtnLoader(true);
    const currentSelectedIds = [...getIds(withLeave, true), ...getIds(withoutLeave, true)];
    // Calculate only newly selected IDs (current selection minus initial selection)
    const newlySelectedIds = currentSelectedIds.filter((id) => !initialSelectedIds.includes(id));

    editLeaveStaffListApi(leaveId, {
      authIds: currentSelectedIds,
      unassignedUserIds: [...getIds(withLeave, false), ...getIds(withoutLeave, false)],
      currantSelectedUserIds: newlySelectedIds,
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) {
          toast.success(res?.message);
          navigate("/leaveTemplate");
        } else toast.error(res?.message);
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
      .finally(() => setBtnLoader(false));
  };

  const totalSelected = getIds(withLeave, true).length + getIds(withoutLeave, true).length;

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(244,63,94,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GitBranch size={20} strokeWidth={1.75} style={{ color: "#f43f5e" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {leaveTemplate.MANAGE_STAFF_LIST}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              Select staff members to assign to this leave template
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {totalSelected > 0 && (
            <span
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: "0.75rem",
                fontWeight: 700,
                background: "rgba(99,102,241,0.1)",
                color: "var(--brand-indigo)",
              }}
            >
              {totalSelected} selected
            </span>
          )}
          <BackButton onClick={() => navigate("/leaveTemplate")} />
        </div>
      </div>

      {/* Two-panel grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}
      >
        <StaffAssignPanel
          title={leaveTemplate.STAFF_WITH_LEAVE}
          variant="with"
          items={withLeave}
          searchValue={searchWith}
          onSearch={setSearchWith}
          onToggle={(s) => toggle(s, true)}
          onCheckAll={() => checkAll(true)}
          allChecked={withLeave.length > 0 && withLeave.every((s) => s.selected)}
          deactiveLabel={leaveTemplate.Deactive}
        />
        <StaffAssignPanel
          title={leaveTemplate.STAFF_WITHOUT_LEAVE}
          variant="without"
          items={withoutLeave}
          searchValue={searchWithout}
          onSearch={setSearchWithout}
          onToggle={(s) => toggle(s, false)}
          onCheckAll={() => checkAll(false)}
          allChecked={withoutLeave.length > 0 && withoutLeave.every((s) => s.selected)}
          deactiveLabel={leaveTemplate.Deactive}
        />
      </div>

      {/* Save */}
      <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 8 }}>
        <BaseButton color="primary" onClick={handleSave} loader={btnLoader} disabled={btnLoader}>
          {leaveTemplate.SAVE}
        </BaseButton>
      </div>
    </div>
  );
};

export default ManageLeaveStaff;
